import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from 'react-icons/md';
import { useHistory } from 'react-router';
import { useLocation } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';

import { routePaths } from 'src/services/routing/routePaths';
import { useTicketTableContext } from 'src/components/followUp/TicketFilterContext';

import useTicketTablePagination from '../TicketTable/TicketTablePagination/useTicketTablePagination';

import { styles } from './ticketDetail.styles';

const SEARCH_PARAMS_KEY = 'ticket';

const ModalHeader = () => {
  const { t } = useTranslation('common');
  const history = useHistory();
  const {
    selectedTicket: ticket,
    tickets,
    setPage,
    page,
  } = useTicketTableContext();
  const { nextTicket, previousTicket } = useTicketTablePagination();
  const searchParams = new URLSearchParams(useLocation().search);

  // Until ticket is loaded, display partial data from list endpoint
  const selectedTicketId = searchParams.get(SEARCH_PARAMS_KEY);
  const partialTicketData = tickets?.find(
    ({ id }) => String(id) === selectedTicketId,
  );

  const updatePage = useCallback(
    (newPage: number) => {
      if (newPage !== page) setPage(newPage);
    },
    [page],
  );

  const selectPreviousTicket = useCallback(() => {
    if (previousTicket?.id === undefined) return;
    updatePage(previousTicket.page);
    history.push({
      pathname: routePaths.TICKET_OVERVIEW,
      search: `?ticket=${previousTicket.id}`,
    });
  }, [previousTicket, searchParams, updatePage]);

  const selectNextTicket = useCallback(() => {
    if (nextTicket?.id === undefined) return;
    updatePage(nextTicket.page);
    history.push({
      pathname: routePaths.TICKET_OVERVIEW,
      search: `?ticket=${nextTicket.id}`,
    });
  }, [nextTicket, searchParams, updatePage]);

  const ticketId = useMemo(
    () => ticket?.id ?? partialTicketData?.id,
    [ticket, partialTicketData],
  );

  return (
    <Box sx={styles.modalHeader}>
      <Box display="flex" alignItems="center" gap={2}>
        <Button
          onClick={selectPreviousTicket}
          disabled={previousTicket === undefined}
        >
          <MdKeyboardArrowLeft size="24" style={{ marginRight: 4 }} />
          {t('Previous')}
        </Button>
        <Button onClick={selectNextTicket} disabled={nextTicket === undefined}>
          {t('Next')}
          <MdKeyboardArrowRight size="24" style={{ marginLeft: 4 }} />
        </Button>
        <Typography sx={(theme) => ({ color: theme.colors.neutral[500] })}>
          {!!ticketId && t('id', { id: ticketId })}
        </Typography>
        <DialogTitle variant="h4">
          {ticket?.respondent?.name ?? partialTicketData?.respondentName}
        </DialogTitle>
      </Box>
      <IconButton onClick={() => history.replace({ search: '' })}>
        <CloseIcon fontSize="small" color="primary" />
      </IconButton>
    </Box>
  );
};

export default ModalHeader;
